// The v1 shell: a stage area, a screen host, a sheet host and toasts, inside a
// 430px column that respects the safe areas (PLAN-03 §4, U1).
//
// The stage is a background layer, not a child of a screen. The Pixi world has
// to stay alive while a sheet is over the coop screen, and an opaque screen
// only hides it — which is also how the renderer gets paused.

import { h } from './dom';
import type { MountedScreen, Transition } from './router';

export interface Shell {
  readonly root: HTMLDivElement;
  readonly column: HTMLDivElement;
  /** Host for the Pixi canvas. */
  readonly stage: HTMLDivElement;
  /** Host for the current screen element. */
  readonly screens: HTMLDivElement;
  /** Host for bottom sheets. */
  readonly sheets: HTMLDivElement;
  readonly toasts: HTMLDivElement;
  /**
   * Shows or pauses the world. Pausing rather than hiding is the point: a
   * hidden canvas still costs the phone a composited layer per frame.
   */
  setStageActive(active: boolean): void;
  /**
   * Told whenever the stage is shown or covered, so the session can stop the
   * renderer without the shell knowing what a renderer is.
   */
  onStageChange(listener: (active: boolean) => void): void;
  /** Router host: swaps the visible screen with the right transition. */
  mountScreen(entry: MountedScreen<HTMLElement>, transition: Transition): void;
  unmountScreen(): void;
  toast(message: string, options?: { actionLabel?: string; onAction?: () => void; ms?: number }): void;
}

function transitionClass(transition: Transition): string {
  if (transition === 'forward') return 'cf-screen--enter-forward';
  if (transition === 'back') return 'cf-screen--enter-back';
  return '';
}

/**
 * Mounts the shell into `host` (normally the body). Existing children of the
 * host are removed: the v2 dev route replaces the desktop overlay rather than
 * layering on top of it.
 */
export function createShell(host: HTMLElement = document.body): Shell {
  const stage = h('div', { class: 'cf-app__stage', attrs: { 'aria-hidden': 'true' } });
  const screens = h('div', { class: 'cf-app__screens' });
  const sheets = h('div', { class: 'cf-app__sheets' });
  const toasts = h('div', {
    class: 'cf-app__toasts',
    attrs: { role: 'status', 'aria-live': 'polite' },
  });
  const column = h('div', { class: 'cf-app__column' }, stage, screens, sheets, toasts);
  const root = h('div', { class: 'cf-app', attrs: { 'data-app': 'v2' } }, column);

  host.replaceChildren(root);

  let stageActive = true;
  let stageListener: ((active: boolean) => void) | null = null;

  return {
    root,
    column,
    stage,
    screens,
    sheets,
    toasts,
    setStageActive(active: boolean): void {
      if (active === stageActive) return;
      stageActive = active;
      stage.dataset.active = String(active);
      stageListener?.(active);
    },
    onStageChange(listener: (active: boolean) => void): void {
      stageListener = listener;
      listener(stageActive);
    },
    mountScreen(entry, transition): void {
      screens.replaceChildren(entry.element);
      if (entry.element.classList) {
        const cls = transitionClass(transition);
        if (cls) {
          entry.element.classList.add(cls);
          // The class is what starts the animation; leaving it on would replay
          // the slide on every later class change.
          window.setTimeout(() => entry.element.classList.remove(cls), 400);
        }
      }
      this.setStageActive(entry.showsStage);
      entry.element.focus?.();
    },
    unmountScreen(): void {
      screens.replaceChildren();
    },
    toast(message, options = {}): void {
      const card = h('div', { class: 'cf-toast' }, h('span', { class: 'cf-grow', text: message }));
      if (options.actionLabel && options.onAction) {
        card.appendChild(
          h(
            'button',
            {
              class: 'cf-btn cf-btn--link',
              text: options.actionLabel,
              on: {
                click: () => {
                  options.onAction?.();
                  card.remove();
                },
              },
            },
          ),
        );
      }
      toasts.appendChild(card);
      // A flock in trouble fires several at once; the oldest goes, not a queue.
      while (toasts.childElementCount > 3) toasts.firstElementChild?.remove();
      window.setTimeout(() => card.remove(), options.ms ?? 6000);
    },
  };
}
